import { FC, Fragment, useEffect } from 'react';
import { capitalCase } from 'change-case';
import { TbUsers } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { VirtualizedList as List, PlatformAlert, SkeletonList } from '@/shared';

import { InvitedMemberListItem, MemberListItem, TeamListItem } from './components';

interface ResultsListProps {
    input: string;
    setInput: any;
    collaborators: any;
    results: any;
    loading: boolean;
    resource: any;
    resourceType: any;
    canInvite: boolean;
    canManage: boolean;
}

const ResultsList: FC<ResultsListProps> = ({
    input,
    setInput,
    collaborators,
    results,
    loading,
    resource,
    resourceType,
    canInvite,
    canManage,
}): JSX.Element => {
    useEffect(() => {}, [input]);

    const permissionOrder: Record<string, number> = {
        Owner: 0,
        Edit: 1,
        Comment: 2,
        View: 3,
    };

    const sortedInvitedCollaborators = [...collaborators]?.sort(
        (a: any, b: any) => permissionOrder[a.permission] - permissionOrder[b.permission],
    );

    const { token } = useAntDesignTheme();

    return (
        <>
            <div style={{ minHeight: '12rem', maxHeight: '20rem', overflowY: 'auto' }}>
                {/* Loading */}
                {loading && <SkeletonList listLength={8} />}
                {/* Members */}
                {results?.members?.length !== 0 && input !== '' && !loading && (
                    <>
                        {/* <Button size="small" style={{ color: token.colorTextTertiary, marginTop: '0.25rem' }} type="text">
                            <small style={{ verticalAlign: '0.05rem' }}>
                                <TbUser size="1rem" style={{ verticalAlign: '-0.225rem', marginRight: '0.25rem' }} />
                                Members
                            </small>
                        </Button> */}
                        {results?.members ? (
                            <List
                                dataSource={results?.members}
                                renderItem={(result: any) => (
                                    <Fragment>
                                        <MemberListItem
                                            resource={resource}
                                            resourceType={resourceType}
                                            result={result}
                                            invites={collaborators}
                                            input={input}
                                            setInput={setInput}
                                            canInvite={canInvite}
                                        />
                                    </Fragment>
                                )}
                            />
                        ) : (
                            <SkeletonList listLength={3} />
                        )}
                    </>
                )}
                {/* Teams */}
                {results?.teams?.length !== 0 && input !== '' && !loading && (
                    <>
                        <div style={{ marginTop: '0.5rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>
                                <TbUsers size='1rem' style={{ verticalAlign: '-0.225rem', marginRight: '0.25rem' }} />
                                Teams
                            </small>
                        </div>
                        {results?.teams ? (
                            <List
                                dataSource={results?.teams}
                                renderItem={(result: any) => (
                                    <Fragment>
                                        <TeamListItem
                                            result={result}
                                            resource={resource}
                                            resourceType={resourceType}
                                            canInvite={canInvite}
                                            setInput={setInput}
                                        />
                                    </Fragment>
                                )}
                            />
                        ) : (
                            <SkeletonList listLength={2} />
                        )}
                    </>
                )}
                {/* Email Invite */}
                {/* !TODO: We want to get around to doing this properly */}
                {/* {results?.members?.length === 0 && input !== '' && !loading && <EmailListItem input={input} />} */}
                {results?.members?.length === 0 && results?.teams?.length === 0 && input !== '' && !loading && (
                    <PlatformAlert
                        status='warning'
                        title='Workspace membership required'
                        description={
                            <>
                                There's no user in this workspace with this name or email address. Invite them to the
                                workspace first, and then invite them to collaborate on{' '}
                                {resourceType && capitalCase(resourceType)}.
                            </>
                        }
                        styles={{ marginTop: '1rem' }}
                    />
                )}
                {/* List of Invited Members */}
                {input === '' && !loading && (
                    <>
                        <div style={{ marginTop: '0.5rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>Invited Members</small>
                        </div>
                        <List
                            dataSource={sortedInvitedCollaborators}
                            renderItem={(collaborator: any) => (
                                <InvitedMemberListItem
                                    key={collaborator?.id}
                                    resourceType={resourceType} // !
                                    resource={resource}
                                    collaborator={collaborator}
                                    collaborators={collaborators}
                                    canManage={canManage}
                                />
                            )}
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default ResultsList;
